"use client";

// Cookie consent banner + Consent Mode v2 bridge.
//
// Purely additive: renders a compact, non-blocking card anchored bottom-left.
// It never locks scroll, never dims the page, and lives at z-index 9200 —
// below the cinematic intro (#cin-intro, z 10000) and the custom cursor
// (z 10009), so it stays hidden during the homepage intro and the cursor
// still floats over it. Existing UI/UX is unaffected.
//
// The deny-by-default consent state is set in app/layout.tsx (beforeInteractive)
// so no tag fires before a choice. This component only *updates* consent when
// the visitor accepts/declines, and persists the choice for repeat visits.

import { useEffect, useState } from "react";

const KEY = "lexvio:consent";

// Version of the cookies/consent policy the visitor's choice was given
// against. Bump when /legal/cookies changes materially — a stored choice made
// under an older policy still counts (we only ever narrowed what Accept
// grants), but the record should say which text was on screen.
const POLICY_VERSION = "2026-08-31";

/**
 * What we persist. GDPR Art. 7(1) asks us to be able to demonstrate consent —
 * for an anonymous marketing visitor that demonstration is this record: the
 * choice, when it was made, and which policy version it was made against.
 * (Older builds stored the bare strings "granted"/"denied"; readChoice keeps
 * accepting them so nobody is re-prompted by this upgrade.)
 */
interface StoredConsent {
  v: 1;
  choice: "granted" | "denied";
  at: string;
  policy: string;
}

function readChoice(raw: string | null): "granted" | "denied" | null {
  if (raw === "granted" || raw === "denied") return raw; // legacy format
  if (raw && raw.startsWith("{")) {
    try {
      const rec = JSON.parse(raw) as Partial<StoredConsent>;
      if (rec.choice === "granted" || rec.choice === "denied") return rec.choice;
    } catch {
      /* unreadable — treat as undecided */
    }
  }
  return null;
}

// Accept grants ANALYTICS ONLY. The ad_* purposes stay denied in every state —
// the Cookies Policy states "we do not set marketing or advertising cookies",
// and a consent payload that granted ad_storage/ad_personalization would
// contradict that published representation.
const GRANTED = {
  ad_storage: "denied",
  analytics_storage: "granted",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const;

const DENIED = {
  ad_storage: "denied",
  analytics_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const;

type Gtag = (...args: unknown[]) => void;

/**
 * Do-Not-Track / Global Privacy Control. The Cookies Policy §5 promises that
 * when either signal is set, analytics never loads regardless of banner state
 * — so with a signal present we neither show the banner (there is nothing to
 * ask) nor ever send a granted update. Mirrors the check in app/layout.tsx's
 * consent-default script, which guards the returning-visitor restore path.
 */
function privacySignalSet(): boolean {
  try {
    const n = navigator as Navigator & {
      globalPrivacyControl?: boolean;
      msDoNotTrack?: string;
    };
    const w = window as Window & { doNotTrack?: string };
    return (
      n.globalPrivacyControl === true ||
      n.doNotTrack === "1" ||
      w.doNotTrack === "1" ||
      n.msDoNotTrack === "1"
    );
  } catch {
    return false;
  }
}

export default function CookieConsent() {
  // `needed` = a banner should be in the DOM; `entered` drives the slide-in.
  // Both start false so SSR and first client render agree (no hydration flash).
  const [needed, setNeeded] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    // Mount the card, then flip `entered` on the next frame so the slide-in
    // transition actually runs (mounting straight into the final state would
    // skip it). All state updates happen inside async callbacks (timeout / rAF
    // / event), never synchronously in the effect body.
    const reveal = () => {
      setNeeded(true);
      requestAnimationFrame(() => setEntered(true));
    };

    // DNT / GPC set: analytics will never load, so there is no choice to ask
    // for. Don't mount the banner at all (the "Cookie settings" reopen event
    // is also moot — the answer is already "denied" and cannot change here).
    if (privacySignalSet()) return;

    // Allow a "Cookie settings" control anywhere to reopen the banner:
    //   window.dispatchEvent(new Event("lexvio:open-consent"))
    window.addEventListener("lexvio:open-consent", reveal);

    let stored: string | null = null;
    try {
      stored = localStorage.getItem(KEY);
    } catch {
      /* storage blocked — treat as undecided */
    }

    let showTimer: ReturnType<typeof setTimeout> | undefined;
    if (readChoice(stored) === null) {
      // Small delay so it slides in gracefully after first paint (and, on the
      // homepage, after the intro curtain has already covered the screen).
      showTimer = setTimeout(reveal, 650);
    }

    return () => {
      window.removeEventListener("lexvio:open-consent", reveal);
      if (showTimer) clearTimeout(showTimer);
    };
  }, []);

  function decide(accepted: boolean) {
    try {
      const record: StoredConsent = {
        v: 1,
        choice: accepted ? "granted" : "denied",
        at: new Date().toISOString(),
        policy: POLICY_VERSION,
      };
      localStorage.setItem(KEY, JSON.stringify(record));
    } catch {
      /* ignore */
    }
    try {
      const gtag = (window as unknown as { gtag?: Gtag }).gtag;
      gtag?.("consent", "update", accepted ? GRANTED : DENIED);
    } catch {
      /* gtag not present (e.g. analytics disabled) — choice is still stored */
    }
    setEntered(false);
    // Remove from the DOM once the slide-out transition finishes.
    setTimeout(() => setNeeded(false), 500);
  }

  if (!needed) return null;

  return (
    <div
      aria-label="Cookie consent"
      aria-live="polite"
      className={`lv-cc${entered ? " in" : ""}`}
      role="dialog"
    >
      <div className="lv-cc-t">Cookies &amp; privacy</div>
      <p className="lv-cc-p">
        We use essential cookies to run LexVio, plus optional analytics cookies to
        improve it — only if you allow. See our{" "}
        <a href="/legal/cookies">Cookie Policy</a> and{" "}
        <a href="/legal/privacy">Privacy Policy</a>.
      </p>
      {/* Decline and Accept carry the same visual weight deliberately —
          refusing must be as easy as accepting (EDPB / CNIL line on consent
          banners). Don't reintroduce a primary-styled Accept. */}
      <div className="lv-cc-row">
        <button className="lv-cc-btn" type="button" onClick={() => decide(false)}>
          Decline
        </button>
        <button className="lv-cc-btn" type="button" onClick={() => decide(true)}>
          Accept
        </button>
      </div>
    </div>
  );
}
